import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/App';
import { useT } from '@/i18n';
import {
  LayoutDashboard,
  Building2,
  CalendarDays,
  BookOpen,
  Users,
  Wallet,
  UserCog,
  LogOut,
  Home,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Settings,
  User,
  Search,
  UsersRound,
  Receipt,
  Landmark
} from 'lucide-react';

const Sidebar = ({ user, isCollapsed, setIsCollapsed }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const t = useT();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => location.pathname.startsWith(path);

  // Admin / CS / Owner / Customer link sets. Labels are resolved at render
  // time via the i18n hook so they update immediately when the user switches
  // language. Admin routes are pinned to English via I18nScope in App.js —
  // the sidebar is rendered inside that scope too, so admin users see these
  // sidebar labels in English regardless of their preferred language.
  const adminLinks = [
    { path: '/dashboard', label: t('sidebar.dashboard'),  icon: LayoutDashboard },
    { path: '/properties', label: t('sidebar.properties'), icon: Building2 },
    { path: '/sales',     label: 'Sales',                 icon: Landmark },
    { path: '/bookings',  label: t('sidebar.bookings'),   icon: BookOpen },
    { path: '/calendar',  label: t('sidebar.calendar'),   icon: CalendarDays },
    { path: '/owners',    label: t('sidebar.owners'),     icon: Users },
    { path: '/payouts',   label: t('sidebar.payouts'),    icon: Wallet },
    { path: '/billing',   label: t('sidebar.billing'),    icon: Receipt },
    { path: '/users',     label: t('sidebar.users'),      icon: UserCog },
    { path: '/team',      label: t('sidebar.team'),       icon: UsersRound },
    { path: '/settings',  label: t('sidebar.settings'),   icon: Settings },
  ];

  const customerServiceLinks = [
    { path: '/dashboard', label: t('sidebar.dashboard'),  icon: LayoutDashboard },
    { path: '/properties', label: t('sidebar.properties'), icon: Building2 },
    { path: '/bookings',  label: t('sidebar.bookings'),   icon: BookOpen },
    { path: '/calendar',  label: t('sidebar.calendar'),   icon: CalendarDays },
    { path: '/owners',    label: t('sidebar.owners'),     icon: Users },
  ];

  const ownerLinks = [
    { path: '/dashboard',  label: t('sidebar.dashboard'),     icon: LayoutDashboard },
    { path: '/properties', label: t('sidebar.my_properties'), icon: Building2 },
    { path: '/bookings',   label: t('sidebar.my_bookings'),   icon: BookOpen },
    { path: '/browse',     label: t('sidebar.reserve_dates'), icon: CalendarDays },
    { path: '/calendar',   label: t('sidebar.calendar'),      icon: CalendarDays },
    { path: '/payouts',    label: t('sidebar.payouts'),       icon: Wallet },
    { path: '/profile',    label: t('sidebar.profile'),       icon: User },
  ];

  const customerLinks = [
    { path: '/dashboard', label: t('sidebar.dashboard'), icon: LayoutDashboard },
    { path: '/browse',    label: t('sidebar.browse'),    icon: Search },
    { path: '/bookings',  label: t('sidebar.my_bookings'), icon: BookOpen },
    { path: '/profile',   label: t('sidebar.profile'),   icon: User },
  ];

  const getLinks = () => {
    switch (user?.role) {
      case 'ADMIN': return adminLinks;
      case 'CUSTOMER_SERVICE': return customerServiceLinks;
      case 'OWNER': return ownerLinks;
      case 'CUSTOMER': return customerLinks;
      default: return [];
    }
  };

  const links = getLinks();

  const getRoleBadgeColor = () => {
    switch (user?.role) {
      case 'ADMIN': return 'bg-red-500/20 text-red-200';
      case 'CUSTOMER_SERVICE': return 'bg-blue-500/20 text-blue-200';
      case 'OWNER': return 'bg-green-500/20 text-green-200';
      case 'CUSTOMER': return 'bg-purple-500/20 text-purple-200';
      default: return 'bg-gray-500/20 text-gray-200';
    }
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className={`p-4 border-b border-[#e1dad2]/10 ${isCollapsed ? 'px-2' : 'p-6'}`}>
        <Link to="/dashboard" className="flex items-center gap-3" data-testid="sidebar-logo">
          <div className={`${isCollapsed ? 'w-10 h-10' : 'w-10 h-10'} rounded-xl bg-[#194a50] flex items-center justify-center flex-shrink-0`}>
            <Home className="w-5 h-5 text-white" />
          </div>
          {!isCollapsed && (
            <div className="overflow-hidden">
              <h1 className="text-lg font-semibold text-white font-['Outfit'] whitespace-nowrap">{t('sidebar.brand_title')}</h1>
              <span className="text-xs text-[#e1dad2]/60 whitespace-nowrap">{t('sidebar.brand_subtitle')}</span>
            </div>
          )}
        </Link>
      </div>

      {/* Toggle button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="hidden md:flex absolute -right-3 top-20 w-6 h-6 bg-[#194a50] text-white rounded-full items-center justify-center hover:bg-[#10555e] transition-colors z-10 shadow-md"
        data-testid="sidebar-toggle"
      >
        {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      {/* Navigation */}
      <nav className="flex-1 p-2 overflow-y-auto" data-testid="sidebar-nav">
        <ul className="space-y-1">
          {links.map((link) => {
            const Icon = link.icon;
            const active = isActive(link.path);
            return (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`sidebar-link ${active ? 'active' : ''} ${isCollapsed ? 'justify-center px-2' : ''}`}
                  data-testid={`nav-${link.path.slice(1)}`}
                  title={isCollapsed ? link.label : undefined}
                  onClick={() => setMobileOpen(false)}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!isCollapsed && <span>{link.label}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Profile */}
      <div className={`p-4 border-t border-[#e1dad2]/10 ${isCollapsed ? 'px-2' : ''}`}>
        {!isCollapsed ? (
          <>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#194a50] flex items-center justify-center text-white font-medium flex-shrink-0">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor()}`}>
                  {user?.role?.replace('_', ' ')}
                </span>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#e1dad2]/70 hover:text-white hover:bg-[#e1dad2]/10 rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span>{t('sidebar.sign_out')}</span>
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-[#194a50] flex items-center justify-center text-white font-medium" title={user?.name}>
              {user?.name?.charAt(0) || 'U'}
            </div>
            <button
              onClick={logout}
              className="p-2 text-[#e1dad2]/70 hover:text-white hover:bg-[#e1dad2]/10 rounded-lg transition-colors"
              data-testid="logout-btn"
              title={t('sidebar.sign_out')}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-[#0c2431] text-white rounded-lg shadow-lg"
        data-testid="mobile-menu-btn"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Desktop Sidebar */}
      <aside 
        className={`sidebar fixed left-0 top-0 h-full flex-col hidden md:flex transition-all duration-300 ${
          isCollapsed ? 'w-16' : 'w-64'
        }`} 
        data-testid="sidebar"
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <aside 
        className={`sidebar fixed left-0 top-0 h-full w-64 flex flex-col md:hidden z-40 transform transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`} 
        data-testid="sidebar-mobile"
      >
        <SidebarContent />
      </aside>
    </>
  );
};

export default Sidebar;
